import * as R from "ramda";
import * as Promise from "bluebird";
import moment from "moment";
import uuid from "uuid/v1";
import { api } from "../api/api";
import { of, from, Observable, concat, debounce, debounceTime } from "rxjs";
import {
  mergeMap,
  map,
  catchError,
  withLatestFrom,
  switchMap,
} from "rxjs/operators";
import { combineEpics, ofType } from "redux-observable";
import {
  getCustomersDone,
  getCustomerDone,
  setCustomerDone,
  setCustomerError,
  navigateToCustomer,
  customerPrinted,
  divPrinted,
  getCustomers as getCustomersAction,
  exportCustomersDone,
  types,
  deleteCustomerGalleryDone,
  papGetAuditRecords,
  papGetAuditRecordsSuccess,
} from "./bowlers.redux";
import {
  specsheetSetSpecsheets,
  specsheetSetSpecsheet,
  specsheetSetOpenDropdown,
} from "../specsheet/specsheet.redux";
import {
  setCurrentBall,
  setLoadBalls,
  jobSelect,
} from "../arsenal/arsenal.redux";
import { checkSpectrePosLink } from "../plugins/plugin.redux";
import { datasources } from "../db/datasources";
import { lpad } from "../../utils";
import { getPhotoForUserId } from "../../utils/utils";
import specSheetImages from "../specSheetImages/specSheetImages";
import { printTemplate } from "../printTemplate/printTemplate";
import { specsheetParser } from "../specsheet/specsheetParser";

Promise.config({ warnings: false, longStackTraces: false, asyncHooks: false });

const setCustomerEpic = (action$, state$) =>
  action$.pipe(
    ofType(types.setCustomer),
    debounceTime(500),
    mergeMap(({ payload: { bowler } }) => {
      const proccessCustomer = async () => {
        try {
          const { customerDS, customerProshopDS } = datasources;
          if (bowler.id) {
            const updatedBowler = await customerDS.update({
              ...bowler,
              localId: bowler.id,
            });

            return {
              bowler,
              newCustomer: false,
            };
          } else {
            const id = uuid();
            const newBowler = await customerDS.create({
              ...bowler,
              localId: id,
              hand: state$.value.settings.appSettings.systemDefault
                .default_hand,
              style: "traditional",
              proshop_id: state$.value.userSession.proshopId,
            });

            return {
              bowler: { ...newBowler, balls_drilled: [], specsheets: [] },
              newCustomer: true,
            };
          }
        } catch (e) {
          throw e;
        }
      };

      return from(proccessCustomer()).pipe(
        switchMap(({ bowler, newCustomer }) => {
          if (newCustomer) {
            return concat(
              of(getCustomerDone({ customer: bowler })),
              of(setCustomerDone({ bowler })),
              of(navigateToCustomer({ navigateTo: bowler.id }))
            );
          } else {
            return concat(
              of(setCustomerDone({ bowler })),
              of(specsheetSetOpenDropdown({ openDropdown: "" }))
            );
          }
        }),
        catchError((error) => setCustomerError({ error }))
      );
    })
  );

const setCustomerPhotoEpic = (action$, state$) =>
  action$.pipe(
    ofType(types.setCustomerPhoto),
    debounceTime(500),
    mergeMap(({ payload: { imageBase64 } }) => {
      const bowler = state$.value.bowlers.selectedCustomer;
      const userId = state$.value.userSession.userData.id;
      const fileName = `customer_${bowler.id}_${uuid()}.png`;

      const getPhoto = getPhotoForUserId(userId);
      const photo = getPhoto(fileName);

      const savebase64AsImageFile = (fileName, base64) => {
        return new Promise((success, fail) => {
          const { customerDS } = datasources;
          const { getSessionApiUrl, checkOffline, ajaxSession } = api;

          const sendImageToServer = ({ image, imageFileName }) =>
            R.pipeP(
              () => Promise.resolve("images/uploadImageBase64"),
              (url) => {
                return ajaxSession(url, {
                  type: "POST",
                  noWorker: true,
                  data: {
                    image,
                    imageFileName,
                  },
                });
              }
            )();

          const saveFileOnServer = (fileProps) =>
            R.pipeP(checkOffline, (offline) =>
              offline ? Promise.resolve() : sendImageToServer(fileProps)
            )();

          saveFileOnServer({ image: base64, imageFileName: fileName })
            .then(() => {
              return customerDS.update({ ...bowler, photo });
            })
            .then(() => {
              success(getPhoto(fileName));
            })
            .catch(fail);
        });
      };

      return from(savebase64AsImageFile(fileName, imageBase64)).pipe(
        map((photo) => {
          return setCustomerDone({ bowler: { ...bowler, photo } });
        }),
        catchError(() => {
          return of(setCustomerDone({ bowler }));
        })
      );
    })
  );

const papGetAuditRecordsEpic = (action$, state$) =>
  action$.pipe(
    ofType(types.papGetAuditRecords),
    withLatestFrom(state$),
    mergeMap(
      ([
        {
          payload: { customerId },
        },
        state,
      ]) => {
        const { checkOffline, ajaxSession } = api;
        const getAuditRecords = (customerId) =>
          new Promise((success, fail) => {
            const url = `audit/customer/${customerId}`;
            R.pipeP(checkOffline, (offline) =>
              offline
                ? Promise.resolve()
                : ajaxSession(url, {
                    type: "GET",
                  })
            )()
              .then(success)
              .catch(fail);
          });
        console.log("Epic has been called");
        getAuditRecords(customerId);
        return from(getAuditRecords(customerId)).pipe(
          map((papAuditRecords) => {
            const props = ["pap_h", "pap_v", "pap_ud"];
            const filteredRecords = papAuditRecords.map((record) =>
              Object.fromEntries(props.map((key) => [key, record[key]]))
            );
            const uniqueRecords = filteredRecords.filter(
              (record, index, self) => {
                const nextRecord = self[index + 1];
                if (!nextRecord) return true; // Keep the last record

                return !(
                  (record.pap_h === nextRecord.pap_h &&
                    record.pap_v === nextRecord.pap_v &&
                    record.pap_ud === nextRecord.pap_ud) ||
                  record.pap_h === null ||
                  record.pap_v === null ||
                  record.pap_ud === null
                );
              }
            );
            return papGetAuditRecordsSuccess({
              papAuditRecords: uniqueRecords,
            });
          })
        );
      }
    )
  );

const setCustomerGalleryEpic = (action$, state$) =>
  action$.pipe(
    ofType(types.setCustomerGallery),
    withLatestFrom(state$),
    mergeMap(
      ([
        {
          payload: { imageBase64, extension, tag },
        },
        state,
      ]) => {
        const bowler = state$.value.bowlers.selectedCustomer;
        const processPhoto = () =>
          new Promise((success, fail) => {
            const { selectedCustomer } = state.bowlers;
            const images = selectedCustomer?.gallery;
            const currentTime = new Date();
            const formattedDate = moment(currentTime).format("YYYYMMDD");
            const formattedTime = moment(currentTime).format("HHmmss");

            let fileName =
              "bowler_plus_" +
              selectedCustomer.id +
              "_" +
              formattedDate +
              "_" +
              formattedTime +
              "_TAG_" +
              tag +
              `_0000.${extension}`;
            if (images.length > 0 && images[images.length - 1]) {
              let lastFileName = images[images.length - 1];
              if (lastFileName.indexOf("/") >= 0) {
                lastFileName = lastFileName.split("/")[1];
              }
              const lastFileNumber =
                lastFileName
                  .replace(
                    new RegExp(
                      `^bowler_plus_${selectedCustomer.id}_\\d{8}_\\d{6}_TAG_.*?_`
                    ),
                    ""
                  )
                  .split(".")[0] * 1;
              fileName = `bowler_plus_${
                selectedCustomer.id
              }_${formattedDate}_${formattedTime}_TAG_${tag}_${lpad(
                lastFileNumber + 1,
                4
              )}.${extension}`;
            }

            const { getSessionApiUrl, checkOffline, ajaxSession } = api;

            const sendImageToServerInChunks = async ({
              image,
              imageFileName,
            }) => {
              const chunkSize = 10 * 1024 * 1024; // 10MB por chunk
              const totalChunks = Math.ceil(image.length / chunkSize);

              // throw error if the media is larger than 200MB
              if (totalChunks > 20) {
                throw new Error("media is too large");
              }

              for (let i = 0; i < totalChunks; i++) {
                const chunk = image.slice(i * chunkSize, (i + 1) * chunkSize);

                await ajaxSession("images/uploadImageBase64Chunk", {
                  type: "POST",
                  noWorker: true,
                  data: {
                    chunk, // Parte del base64
                    chunkIndex: i, // Índice actual
                    totalChunks, // Cantidad total
                    fileName: imageFileName,
                  },
                });
              }

              return { success: true, message: "Upload completed" };
            };

            // const sendImageToServer = ({ image, imageFileName }) =>
            //   R.pipeP(
            //     () => Promise.resolve("images/uploadImageBase64"),
            //     (url) => {
            //       return ajaxSession(url, {
            //         type: "POST",
            //         noWorker: true,
            //         data: {
            //           image,
            //           imageFileName,
            //         },
            //       });
            //     }
            //   )();

            const saveFileOnServer = (fileProps) =>
              R.pipeP(checkOffline, (offline) =>
                offline
                  ? Promise.resolve()
                  : sendImageToServerInChunks(fileProps)
              )();

            saveFileOnServer({ image: imageBase64, imageFileName: fileName })
              .then(() => {
                success(fileName);
              })
              .catch(fail);
          });
        return from(processPhoto()).pipe(
          map((photo) => {
            return setCustomerDone({
              bowler: { ...bowler, gallery: [...bowler.gallery, photo] },
            });
          }),
          catchError((error) => {
            console.error("Error updating gallery:", error);
            return of(setCustomerError({ error }));
          })
        );
      }
    )
  );

const setCustomerGalleryTagEpic = (action$, state$) =>
  action$.pipe(
    ofType(types.setCustomerGalleryTag),
    withLatestFrom(state$),
    mergeMap(
      ([
        {
          payload: { fileName, tag },
        },
        state,
      ]) => {
        const { customerDS } = datasources;
        const { getSessionApiUrl, checkOffline, ajaxSession } = api;
        const updateTag = (fileName, tag) =>
          new Promise((success, fail) => {
            const updateTagOnServer = (fileToUpdate) =>
              R.pipeP(checkOffline, (offline) =>
                offline
                  ? Promise.resolve()
                  : ajaxSession(fileToUpdate, {
                      type: "PUT",
                      data: {
                        tag,
                      },
                    })
              )();
            const updateTagOnLocal = () =>
              customerDS.update({
                ...state.bowlers.selectedCustomer,
                gallery: state.bowlers.selectedCustomer.gallery.map((item) =>
                  item.file === fileName ? { ...item, tag } : item
                ),
              });
            const updateTagOnLocalAndServer = () =>
              R.pipeP(updateTagOnLocal, (bowler) =>
                updateTagOnServer(`sync/image/${fileName}`)
              )();
            updateTagOnLocalAndServer()
              .then(() => success())
              .catch(fail);
          });
        return from(updateTag(fileName, tag)).pipe(
          map(() => {
            return setCustomerDone({
              bowler: {
                ...state.bowlers.selectedCustomer,
                gallery: state.bowlers.selectedCustomer.gallery.map((item) =>
                  item.file === fileName ? { ...item, tag } : item
                ),
              },
            });
          })
        );
      }
    )
  );

const deleteCustomerGalleryEpic = (action$, state$) =>
  action$.pipe(
    ofType(types.deleteCustomerGallery),
    withLatestFrom(state$),
    mergeMap(
      ([
        {
          payload: { fileName },
        },
        state,
      ]) => {
        const { getSessionApiUrl, checkOffline, ajaxSession } = api;

        const removePhoto = (fileName) =>
          new Promise((success, fail) => {
            const deleteFileOnServer = (fileToDelete) =>
              R.pipeP(checkOffline, (offline) =>
                offline
                  ? Promise.resolve()
                  : ajaxSession(fileToDelete, {
                      type: "DELETE",
                    })
              )();

            deleteFileOnServer(`sync/image/${fileName}`)
              .then(() => success())
              .catch(fail);
          });

        return from(removePhoto(fileName)).pipe(
          map(() => {
            return deleteCustomerGalleryDone({ fileName });
          })
        );
      }
    )
  );

const bowlersRemoveEpic = (action$, state$) =>
  action$.pipe(
    ofType(types.deleteCustomer),
    withLatestFrom(state$),
    mergeMap(
      ([
        {
          payload: { id },
        },
        state,
      ]) => {
        const { customerDS } = datasources;
        return from(customerDS.delete(id)).pipe(
          map(() => {
            return getCustomersAction({ loadMore: false, offset: 0 });
          }),
          catchError(() => {
            return of(getCustomersAction({ loadMore: false, offset: 0 }));
          })
        );
      }
    )
  );

const getCustomers = ({ proshopId, orderBy, limit, offset, filter }) =>
  new Promise((success, fail) => {
    const { customerDS } = datasources;
    customerDS
      .read({ proshopId, orderBy, limit, offset, filter })
      .then((customers) => success(customers))
      .catch((error) => fail(error));
  });

const exportCustomers = ({ proshopId, orderBy, limit, offset, filter }) =>
  new Promise((success, fail) => {
    const { getSessionApiUrl } = api;

    const endPointName = "customer";
    getSessionApiUrl(endPointName, {
      proshopId,
      limit,
      offset,
      filter,
      orderBy,
      // No slashes or colons: they travel to Content-Disposition and the
      // browser then refuses to save the file. "d" was the day of the week
      // on top of that.
      filename: `customers_export_${moment().format("YYYY-MM-DD_HHmm")}.csv`,
    }).then((url) => {
      if (window) {
        window.open(url, "_blank");
      }

      success();
    });
  });

const getCustomer = ({ customer_id }) =>
  new Promise((success, fail) => {
    const { customerDS } = datasources;
    customerDS
      .get(customer_id)
      .then((customer) => {
        return success(customer);
      })
      .catch((error) => fail(error));
  });

const getCustomersEpic = (action$, state$) =>
  action$.pipe(
    ofType(types.getCustomers),
    withLatestFrom(state$),
    mergeMap(([_, state]) => {
      const proshopId = state.userSession.proshopId;

      return from(
        getCustomers({
          proshopId: proshopId,
          limit: state.bowlers.limit,
          offset: state.bowlers.offset,
          filter: state.bowlers.filter,
          orderBy: state.bowlers.orderBy,
        })
      ).pipe(
        map((customers) => {
          return getCustomersDone({
            customers,
          });
        }),
        catchError(() => {
          return of(getCustomersDone({ customers: [] }));
        })
      );
    })
  );

const exportCustomersEpic = (action$, state$) =>
  action$.pipe(
    ofType(types.exportCustomers),
    withLatestFrom(state$),
    mergeMap(([_, state]) => {
      const proshopId = state.userSession.proshopId;

      return from(
        exportCustomers({
          proshopId: proshopId,
          limit: state.bowlers.limit,
          offset: state.bowlers.offset,
          filter: state.bowlers.filter,
          orderBy: state.bowlers.orderBy,
        })
      ).pipe(
        map(() => {
          return exportCustomersDone();
        }),
        catchError(() => {
          return of(exportCustomersDone());
        })
      );
    })
  );

const getCustomerEpic = (action$, state$) =>
  action$.pipe(
    ofType(types.getCustomer),
    mergeMap(({ payload }) => {
      return from(
        getCustomer({
          customer_id: payload.customer_id,
        })
      ).pipe(
        switchMap((customer) => {
          const specsheets = R.sort((s1, s2) => {
            const updateAtS1 =
              typeof s1.updated_at === "string"
                ? moment(s1.updated_at).valueOf()
                : s1.updated_at;
            const updateAtS2 =
              typeof s2.updated_at === "string"
                ? moment(s2.updated_at).valueOf()
                : s2.updated_at;
            return s1.updated_at && s2.updated_at
              ? updateAtS2 - updateAtS1
              : false;
          })(customer?.specsheets || []);

          if (payload.setCurrentball) {
          } else {
          }
          const currentBallId = payload.setCurrentball
            ? payload.setCurrentball
            : customer?.balls_drilled[0]?.id || "";

          const currentBall = (customer?.balls_drilled || []).filter(
            (ball) => ball.id === currentBallId
          )[0];

          const currentJobId = payload.setCurrentJob
            ? payload.setCurrentJob
            : currentBall?.jobs[0]?.id || "";

          const currentJob = (currentBall?.jobs || []).filter(
            (job) => job.id === currentJobId
          )[0];

          let specSheetIdx = 0;
          if (payload.setCurrentSpecsheet) {
            specSheetIdx = specsheets.findIndex(
              (specsheet) => specsheet.id === payload.setCurrentSpecsheet
            );
          }

          return concat(
            of(getCustomerDone({ customer })),
            of(specsheetSetSpecsheets({ specsheets })),
            of(
              specsheetSetSpecsheet({
                specsheet: specsheets[specSheetIdx] || {},
              })
            ),
            of(setLoadBalls({ balls: customer?.balls_drilled || [] })),
            of(setCurrentBall({ ball: currentBall })),
            of(jobSelect({ job: currentJob })),
            of(checkSpectrePosLink({ spectreCustomer: customer }))
          );
        }),
        catchError((error) => {
          return of(
            getCustomerDone({
              customer: null,
              specsheets: [],
              specsheet: {},
              balls: [],
            })
          );
        })
      );
    })
  );

const customerPrintEpic = (action$, state$) =>
  action$.pipe(
    ofType(types.customerPrint),
    withLatestFrom(state$),
    mergeMap(([_, state]) => {
      return from(
        new Promise((success, reject) => {
          const currentCustomer = state.bowlers.selectedCustomer;
          const specSheet = state.specsheet.currentSpecSheet;
          const thumb_info = specSheet.thumb_cuts || [];
          const arsenal_info = currentCustomer.balls_drilled || [];

          try {
            const language = state.settings.language;
            const specsheetImg = specSheetImages[language];
            const formSpecsheet = specsheetParser(state.settings.appSettings)(
              specSheet
            );

            const printData = {
              specsheetImg,
              specSheet: formSpecsheet,
              currentCustomer,
              currentArsenal: arsenal_info[0],
              arsenalDS: arsenal_info,
              currentThumbCuts: thumb_info,
              userId: state.userSession.userData.id,
            };

            const printout = printTemplate(
              R.assoc("contentWithHtml", false, printData)
            );

            const PrintHtml = (html) => {
              var printIFrame = document.createElement("iframe");
              printIFrame.name = "printIFrame";
              printIFrame.style.position = "absolute";
              printIFrame.style.top = "-1000000px";
              document.body.appendChild(printIFrame);
              var frameDoc = printIFrame.contentWindow
                ? printIFrame.contentWindow
                : printIFrame.contentDocument.document
                ? printIFrame.contentDocument.document
                : printIFrame.contentDocument;
              frameDoc.document.open();
              frameDoc.document.write(html);
              frameDoc.document.close();
              setTimeout(() => {
                window.frames["printIFrame"].focus();
                window.frames["printIFrame"].printPage();
                setTimeout(() => {
                  document.body.removeChild(printIFrame);
                }, 10000);
              }, 500);
              return false;
            };

            const PrintHtmlCRIOS = (html) => {
              var newWindow = window.open("", "_blank");
              newWindow.document.write(html);
              setTimeout(() => {
                newWindow.print();
              }, 500);
              return false;
            };

            if (-1 !== navigator.userAgent.toLowerCase().indexOf("crios")) {
              PrintHtmlCRIOS(printout);
            } else {
              PrintHtml(printout);
            }

            success();
          } catch (e) {
            reject(e);
          }
        })
      ).pipe(
        map(() => {
          return customerPrinted();
        }),
        catchError((error) => {
          return customerPrinted();
        })
      );
    })
  );

const divPrintEpic = (action$, state$) =>
  action$.pipe(
    ofType(types.divPrint),
    withLatestFrom(state$),
    mergeMap(([payload, state]) => {
      return from(
        new Promise((success, reject) => {
          try {
            const printout = payload.printout;

            const PrintHtml = (html) => {
              var printIFrame = document.createElement("iframe");
              printIFrame.name = "printIFrame";
              printIFrame.style.position = "absolute";
              printIFrame.style.top = "-1000000px";
              document.body.appendChild(printIFrame);
              var frameDoc = printIFrame.contentWindow
                ? printIFrame.contentWindow
                : printIFrame.contentDocument.document
                ? printIFrame.contentDocument.document
                : printIFrame.contentDocument;
              frameDoc.document.open();
              frameDoc.document.write(html);
              frameDoc.document.close();
              setTimeout(() => {
                window.frames["printIFrame"].focus();
                window.frames["printIFrame"].printPage();
                setTimeout(() => {
                  document.body.removeChild(printIFrame);
                }, 10000);
              }, 500);
              return false;
            };

            const PrintHtmlCRIOS = (html) => {
              var newWindow = window.open("", "_blank");
              newWindow.document.write(html);
              setTimeout(() => {
                newWindow.print();
              }, 500);
              return false;
            };

            if (-1 !== navigator.userAgent.toLowerCase().indexOf("crios")) {
              PrintHtmlCRIOS(printout);
            } else {
              PrintHtml(printout);
            }

            success();
          } catch (e) {
            reject(e);
          }
        })
      ).pipe(
        map(() => {
          return divPrinted();
        }),
        catchError((error) => {
          return divPrinted();
        })
      );
    })
  );

export default combineEpics(
  getCustomersEpic,
  getCustomerEpic,
  setCustomerEpic,
  setCustomerPhotoEpic,
  setCustomerGalleryEpic,
  setCustomerGalleryTagEpic,
  deleteCustomerGalleryEpic,
  customerPrintEpic,
  bowlersRemoveEpic,
  divPrintEpic,
  exportCustomersEpic,
  papGetAuditRecordsEpic
);
